import React, { useMemo } from 'react';
import TSAdvancedHierarchicalScoreTable, {
  ScoreData,
} from '@components/HierarchicalTable/TSAdvancedHierarchicalTable';
import ScrollWrapper from '@components/Atoms/ScrollWrapper';
import { useSectionStudentWithFilter } from '@reducers/sectionStudent/hooks';
import { formatStudentName } from '@utils/studentName';
import ShowIf from '@components/Atoms/ShowIf';
import Skeleton from '@components/Atoms/Skeleton';
import InformationCard from '@components/Component/InformationCard';
import Box from '@components/Atoms/Box';
import Typography from '@components/Atoms/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useGetActiveTestSession } from '../../hooks';
import { useHideEmptyColumns } from '../../contexts/HideEmptyColumns/HideEmptyColumnsContext';
import { HierarchicalScoreCell } from './SharedReportComponents';
import SetThresholdRange from '../../contexts/HighlightedScore/SetThresholdRange';
import { useReportHooks, ReportType } from './useReportHooks';
import { createHierarchicalScore } from './helpers';
import { filterTreeNodes } from './utils/filterNAColumns';

type Props = {
  data: any;
  studentNameMap?: { [studentId: string]: string };
  reportType: ReportType;
  showNoData?: boolean;
  alwaysShowThreshold?: boolean;
};

const getHideEmptyColumnsLabel = (reportType: ReportType): string => {
  const labels: Record<ReportType, string> = {
    lesson: 'Hide lessons not tested on this exam',
    skill: 'Hide skills not tested on this exam',
    standard: 'Hide standards not tested on this exam',
  };
  return labels[reportType];
};

const BaseReport = ({
  data,
  studentNameMap,
  reportType,
  showNoData = false,
  alwaysShowThreshold = false,
}: Props) => {
  const { hideEmptyColumns, setHideEmptyColumns } = useHideEmptyColumns();
  const examSession = useGetActiveTestSession();
  const examId = data?.examId ?? examSession?.exam.id;
  const sectionId = data?.sectionId;
  const hasValidExamId = examId != null && examId > 0;

  const { reportData, isLoading, treeNode } = useReportHooks(
    reportType,
    data.id,
    examId ?? 0,
    sectionId,
    { enabled: hasValidExamId },
  );

  const sectionStudents = useSectionStudentWithFilter(sectionId);
  const hasReportData = reportData && Object.keys(reportData).length > 0;
  const hideOutcomeSlider = data?.hideOutcomeSlider ?? false;

  // Filter tree node to hide empty columns when checkbox is checked
  const filteredTreeNode = useMemo(() => {
    if (!treeNode || !reportData) {
      return treeNode;
    }
    const filtered = filterTreeNodes(treeNode, reportData, hideEmptyColumns);
    // Convert null to undefined for consistency with treeNode type
    return filtered ?? undefined;
  }, [treeNode, reportData, hideEmptyColumns]);

  const tableData = useMemo(() => {
    if (!reportData || !filteredTreeNode) {
      return [];
    }

    const studentsToMap =
      sectionStudents && sectionStudents.length > 0
        ? sectionStudents
        : Object.keys(reportData)
            .map((userId) => {
              const fullName = studentNameMap?.[userId] || `Student ${userId}`;
              const [first_name, ...last_name_parts] = fullName.split(' ');
              const parsedId = parseInt(userId, 10);
              if (isNaN(parsedId)) {
                console.warn(`Invalid student ID: ${userId}`);
                return null;
              }
              return {
                id: parsedId,
                first_name: first_name || 'Student',
                last_name: last_name_parts.join(' ') || '',
                displayName: fullName,
              };
            })
            .filter(Boolean);

    return studentsToMap
      .map((student: any) => {
        const studentReport = reportData[student.id.toString()];

        const rootScore = createHierarchicalScore(filteredTreeNode, studentReport);
        if (!rootScore) {
          console.warn('Failed to create root score for student', student.id);
          return null;
        }

        return {
          id: student.id.toString(),
          name: student.displayName ?? formatStudentName(student),
          score: rootScore,
        } as ScoreData;
      })
      .filter((item): item is ScoreData => item !== null);
  }, [sectionStudents, reportData, filteredTreeNode, studentNameMap]);

  const columnContext = filteredTreeNode ? { [reportType]: filteredTreeNode } : undefined;
  const hasValidData =
    !isLoading && filteredTreeNode && hasReportData && tableData.length > 0;
  const hasNoData =
    !isLoading && (!filteredTreeNode || !hasReportData || tableData.length === 0);
  const shouldShowThreshold = alwaysShowThreshold || !hideOutcomeSlider;
  const shouldShowSkeleton = isLoading || (!showNoData && hasNoData);

  return (
    <ScrollWrapper
      sx={{ height: '100%', width: '100%', minHeight: '37.5rem' }}
      key={data.id}
    >
      <ShowIf If={shouldShowSkeleton}>
        <Skeleton height="100%" width="100%" />
      </ShowIf>

      {showNoData && (
        <ShowIf If={hasNoData}>
          <InformationCard
            img="/images/no-content.png"
            title="No Data Available"
            description="There are no exam results for this subject yet. Students need to complete exams before reports can be generated."
          />
        </ShowIf>
      )}

      <ShowIf If={hasValidData}>
        {shouldShowThreshold && <SetThresholdRange />}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hideEmptyColumns}
                onChange={(e) => setHideEmptyColumns(e.target.checked)}
                aria-label={getHideEmptyColumnsLabel(reportType)}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '14px' }}>
                {getHideEmptyColumnsLabel(reportType)}
              </Typography>
            }
          />
        </Box>
        <TSAdvancedHierarchicalScoreTable
          key={`${data.id}-${hideEmptyColumns}`}
          data={tableData}
          columnContext={columnContext}
          defaultOpenFirstColumn
          columns={[
            {
              id: 'name',
              header: 'Name',
              cell: ({ row }) => row.original.name,
            },
            {
              ctxKey: reportType,
              closedValueGetter: ({ data: rowData, colId }) => {
                if (colId.split('.').length === 1) {
                  return rowData.name;
                }
                return colId;
              },
              cellComponent: HierarchicalScoreCell,
              report: reportData,
            },
          ]}
        />
      </ShowIf>
    </ScrollWrapper>
  );
};

export default BaseReport;
